import {
  useState,
  useCallback,
  useEffect,
  useContext,
  useRef,
  useMemo,
} from "react";
import { GiftIcon } from "./components/icon";
import Modal from "./components/modal";
import { NavigationContext } from "./context/navigation";
import classNames from "./helpers/classNames";
import useButtonTracking from "./hooks/useButtonTracking";
import Subscribe from "./screens/subscribe";
import Thanks from "./screens/thanks";

function App() {
  const [isOpen, setIsOpen] = useState(false);
  const { activeRoute, setActiveRoute } = useContext(NavigationContext);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [hintImpression, hintClick] = useButtonTracking({
    text: "Drop a hint",
    placement: "center",
    targetpath: "#",
  });

  const onClose = useCallback(() => {
    return setIsOpen(!isOpen);
  }, [isOpen]);

  const pdpVersion = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      return "default";
    }

    if (
      window.Bose.DataLayer.Data.Page.primaryCategory === "frames" ||
      [
        "bose_smart_soundbar_300",
        "bose_smart_soundbar_900",
        "bose_portable_home_speaker",
        "soundlink_revolve_ii",
        "soundlink_revolve_plus_ii",
        "bose_home_speaker_500",
        "sport_earbuds",
      ].includes(window.Bose.DataLayer.Data.Product.productCode ?? "")
    ) {
      return "small";
    }

    return "default";
  }, []);

  useEffect(() => {
    // Reset route when closed
    if (!isOpen) {
      setActiveRoute("subscribe");
    }

    if (!buttonRef?.current) return;

    buttonRef.current.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      hintClick();
      return setIsOpen(true);
    });
  }, [isOpen, setActiveRoute, hintClick]);

  return (
    <div>
      <div {...hintImpression}>
        <button
          ref={buttonRef}
          className={classNames(
            "bg-gray-100 inline-flex items-center space-x-3 px-6 rounded-full border border-black ml-3 cursor-pointer z-50",
            pdpVersion === "default" ? "py-3.5" : "py-3"
          )}
          type="button"
        >
          <GiftIcon />
          <span className="font-bold uppercase text-lg leading-3 pt-1">
            Drop a hint
          </span>
        </button>
      </div>

      <Modal isOpen={isOpen} onClose={onClose}>
        {activeRoute === "subscribe" && <Subscribe />}
        {activeRoute === "thanks" && <Thanks setIsOpen={setIsOpen} />}
      </Modal>
    </div>
  );
}

export default App;
